import React, { useEffect, useState } from 'react';
import { Bid, BidInput, PriceType } from '../../api/marketplace';

export const MAX_MESSAGE_LENGTH = 2000;

// Euros as typed ("450", "450,50", "1.234,56", "1,234.56", "1.200") to integer
// cents, or null when the input is not a positive amount. With both separators
// the later one is the decimal mark. With one kind, it is a thousands separator
// when it repeats or is followed by exactly three digits.
export const centsFromEuros = (value: string): number | null => {
  let text = value.replace(/[\s€]/g, '');
  if (!text) return null;
  const lastComma = text.lastIndexOf(',');
  const lastDot = text.lastIndexOf('.');
  if (lastComma >= 0 && lastDot >= 0) {
    const [thousands, decimal] = lastComma > lastDot ? [/\./g, ','] : [/,/g, '.'];
    text = text.replace(thousands, '').replace(decimal, '.');
  } else if (lastComma >= 0 || lastDot >= 0) {
    const separator = lastComma >= 0 ? ',' : '.';
    const parts = text.split(separator);
    const isThousands = parts.length > 2 || parts[parts.length - 1].length === 3;
    text = isThousands ? parts.join('') : parts.join('.');
  }
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;
  const cents = Math.round(parseFloat(text) * 100);
  return cents > 0 ? cents : null;
};

const eurosFromCents = (cents: number): string => (cents / 100).toFixed(2);

interface BidFormProps {
  existingBid: Bid | null;
  onSubmit: (bid: BidInput) => Promise<void>;
  onWithdraw: () => Promise<void>;
}

// Create, edit or withdraw this firm's bid on an open request. The parent only
// renders it while the request is OPEN and before the bid deadline.
const BidForm: React.FC<BidFormProps> = ({ existingBid, onSubmit, onWithdraw }) => {
  const active = existingBid && existingBid.status === 'submitted' ? existingBid : null;
  const [price, setPrice] = useState(active ? eurosFromCents(active.priceCents) : '');
  const [priceType, setPriceType] = useState<PriceType>(active?.priceType || 'fixed');
  const [etaDays, setEtaDays] = useState(active ? String(active.etaDays) : '');
  const [message, setMessage] = useState(active?.message || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!active) return;
    setPrice(eurosFromCents(active.priceCents));
    setPriceType(active.priceType);
    setEtaDays(String(active.etaDays));
    setMessage(active.message || '');
  }, [active]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setNotice('');
    const priceCents = centsFromEuros(price);
    if (priceCents === null) {
      setError('Enter a price in euros greater than 0.');
      return;
    }
    const eta = Number(etaDays);
    if (!Number.isInteger(eta) || eta < 1) {
      setError('Enter the estimated time as a whole number of days (at least 1).');
      return;
    }
    if (message.length > MAX_MESSAGE_LENGTH) {
      setError(`Keep the message under ${MAX_MESSAGE_LENGTH} characters.`);
      return;
    }
    setBusy(true);
    try {
      await onSubmit({ priceCents, priceType, etaDays: eta, message: message.trim() });
      setNotice(active ? 'Bid updated.' : 'Bid submitted.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save your bid.');
    } finally {
      setBusy(false);
    }
  };

  const handleWithdraw = async () => {
    if (!window.confirm('Withdraw your bid? You can bid again while the request is open.')) return;
    setError('');
    setNotice('');
    setBusy(true);
    try {
      await onWithdraw();
      setNotice('Bid withdrawn.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to withdraw your bid.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="marketplace-form bid-form" onSubmit={handleSubmit} aria-label="Bid">
      <h3>{active ? 'Your bid' : 'Place a bid'}</h3>
      {error && <div className="status-error-banner" role="alert">{error}</div>}
      {notice && <div className="marketplace-notice" role="status">{notice}</div>}
      <div className="marketplace-form-row">
        <label>
          Price (EUR)
          <input
            type="text"
            inputMode="decimal"
            value={price}
            onChange={e => setPrice(e.target.value)}
            placeholder="e.g. 450"
            required
          />
        </label>
        <label>
          Price type
          <select value={priceType} onChange={e => setPriceType(e.target.value as PriceType)}>
            <option value="fixed">Fixed price</option>
            <option value="from">From (starting price)</option>
          </select>
        </label>
        <label>
          ETA (days)
          <input
            type="number"
            min={1}
            step={1}
            value={etaDays}
            onChange={e => setEtaDays(e.target.value)}
            required
          />
        </label>
      </div>
      <div className="marketplace-field">
        <label>
          Message to the customer
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={5}
            maxLength={MAX_MESSAGE_LENGTH}
            placeholder="What is included, what you need from the customer, how you work."
          />
        </label>
        <span className="marketplace-char-count">
          {message.length}/{MAX_MESSAGE_LENGTH}
        </span>
      </div>
      <div className="marketplace-actions">
        <button type="submit" className="download-button" disabled={busy}>
          {active ? 'Update bid' : 'Submit bid'}
        </button>
        {active && (
          <button type="button" className="logout-button" onClick={handleWithdraw} disabled={busy}>
            Withdraw bid
          </button>
        )}
      </div>
    </form>
  );
};

export default BidForm;
